/**
 * Обратная связь на экране · окно 5 запуска — два куска, живущих внутри других экранов.
 *
 *   1. КАРТОЧКА СПРОСА («Как тебе Lang?») — в кабинете, и только когда ядро сказало
 *      «пора» (7 дней занятий, потом раз в 3 месяца). ⛔ Витрина сама момент не решает.
 *   2. ФОРМА ЗАЯВКИ («Что-то не работает?») — в настройках, доступна всегда.
 *      ⭐ Человек пишет одну строку; экран, слово, задание и ответ сервера приложение
 *      добавляет само (`src/feedback.ts`) и честно показывает, что именно приложит.
 *
 * Тексты и раскладка живут в `src/feedback.ts` и проверяются запуском:
 *   cd Lang/web && node tools/check-feedback.mjs
 */

import "../styles/feedback.css";

import {
  ASK_HINT, ASK_LATER, ASK_THANKS, ASK_TITLE, ASK_WISH, REPORT_HINT, REPORT_SENT,
  REPORT_TITLE, STAR_WORD, STARS_MAX, STATUS_HINT, STATUS_ORDER, TEXT_MAX,
  describeContext, forgetServerError, snapshot, statusLabel, statusStep, ticketValid,
  type Status,
} from "../feedback";
import { rate, rateLater, reportProblem, state } from "../state";
import { render } from "../router";
import { tap } from "../tg";

export function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

/* ── 1. карточка спроса оценки ─────────────────────────────────────────────── */

let picked = 0;          // выбранная звезда до отправки
let thanked = false;     // сказали «спасибо» — карточка гаснет до следующего захода

/** Спрашивать ли сейчас. ⭐ Решение принимает ЯДРО, здесь только читаем его ответ. */
export function shouldAsk(): boolean {
  if (thanked) return false;
  return Boolean(state.feedback?.ask?.ask ?? state.me?.feedback?.ask);
}

function starsHtml(): string {
  let out = '<div class="stars" role="group" aria-label="Оценка">';
  for (let i = 1; i <= STARS_MAX; i++) {
    out += `<button type="button" class="star${i <= picked ? " on" : ""}" data-star="${i}" ` +
           `aria-label="${i} из ${STARS_MAX}">★</button>`;
  }
  return out + "</div>";
}

export function askCardHtml(): string {
  if (thanked) return `<div class="card ask"><p class="h">${esc(ASK_THANKS)}</p></div>`;
  return (
    '<div class="card ask" id="askCard">' +
      `<p class="h">${esc(ASK_TITLE)}</p>` +
      `<p class="hsub">${esc(ASK_HINT)}</p>` +
      starsHtml() +
      (picked ? `<p class="starword">${esc(STAR_WORD[picked])}</p>` : "") +
      (picked
        ? `<textarea class="wish" id="askWish" rows="2" maxlength="${TEXT_MAX}" ` +
          `placeholder="${esc(ASK_WISH)}"></textarea>` +
          '<div class="askrow"><button type="button" class="askbtn yes" id="askSend">' +
          "Отправить</button>" +
          `<button type="button" class="askbtn no" id="askLater">${esc(ASK_LATER)}</button></div>`
        : `<div class="askrow"><button type="button" class="askbtn no" id="askLater">` +
          `${esc(ASK_LATER)}</button></div>`) +
    "</div>"
  );
}

/** Повесить обработчики карточки спроса. `screen` — что перерисовывать после нажатий. */
export function bindAsk(host: HTMLElement, screen: "cabinet"): void {
  host.querySelector(".stars")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-star]");
    if (!btn) return;
    tap(btn);
    picked = Number(btn.dataset.star);
    render(screen);                       // звёзды и поле пожелания появляются сразу
  });
  host.querySelector("#askSend")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    const wish = host.querySelector<HTMLTextAreaElement>("#askWish")?.value ?? "";
    thanked = true;
    void rate(picked, wish.trim() || undefined).then(() => render(screen));
    render(screen);
  });
  host.querySelector("#askLater")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    // ⛔ «Спасибо» на отказ не говорим — человек не оценивал, это было бы враньём.
    void rateLater().then(() => render(screen));
    render(screen);
  });
}

/* ── 2. форма заявки «не работает» ─────────────────────────────────────────── */

let reportOpen = false;
let reportSent = false;
let reportFailed = false;

function myTicketsHtml(): string {
  const mine = state.feedback?.my ?? [];
  if (!mine.length) return "";
  const rows = mine.slice(0, 5).map((t) => {
    const step = statusStep(t.status);
    const dots = STATUS_ORDER.map((s, i) =>
      `<span class="dot${i < step ? " on" : ""}" title="${esc(statusLabel(s))}"></span>`).join("");
    return (
      '<div class="tk">' +
        `<div class="tktext">${esc(t.text)}</div>` +
        `<div class="tkstate"><span class="tkstatus">${esc(t.status_label)}</span>${dots}</div>` +
        `<div class="tkhint">${esc(STATUS_HINT[t.status as Status] ?? "")}</div>` +
        (t.answer ? `<div class="tkanswer">${esc(t.answer)}</div>` : "") +
      "</div>"
    );
  }).join("");
  return '<div class="mytickets"><p class="hsub">Твои заявки</p>' + rows + "</div>";
}

export function reportCardHtml(): string {
  const ctx = describeContext(snapshot());
  return (
    '<div class="card report" id="reportCard">' +
      `<p class="h">${esc(REPORT_TITLE)}</p>` +
      `<p class="hsub">${esc(REPORT_HINT)}</p>` +
      (reportSent ? `<p class="sent">${esc(REPORT_SENT)}</p>` : "") +
      (reportFailed ? '<p class="sent bad">Не отправилось — связь пропала. ' +
                      "Попробуй ещё раз, текст на месте.</p>" : "") +
      (reportOpen
        ? `<textarea class="wish" id="reportText" rows="3" maxlength="${TEXT_MAX}" ` +
          'placeholder="Что случилось? Например: «нажал ответ — ничего не произошло»"></textarea>' +
          '<div class="ctxbox"><p class="ctxh">Приложим сами:</p><ul>' +
          ctx.map((line) => `<li>${esc(line)}</li>`).join("") +
          "</ul></div>" +
          '<div class="askrow"><button type="button" class="askbtn yes" id="reportSend">' +
          'Отправить</button><button type="button" class="askbtn no" id="reportClose">' +
          "Отмена</button></div>"
        : '<div class="askrow"><button type="button" class="askbtn no" id="reportOpen">' +
          "Написать о проблеме</button></div>") +
      myTicketsHtml() +
    "</div>"
  );
}

export function bindReport(host: HTMLElement, screen: "settings"): void {
  host.querySelector("#reportOpen")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    reportOpen = true;
    reportSent = false;
    reportFailed = false;
    render(screen);
  });
  host.querySelector("#reportClose")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    reportOpen = false;
    render(screen);
  });
  host.querySelector("#reportSend")?.addEventListener("click", (e) => {
    const field = host.querySelector<HTMLTextAreaElement>("#reportText");
    const text = field?.value ?? "";
    if (!ticketValid(text)) {              // ⛔ пустую не шлём: ядро её всё равно не примет
      field?.focus();
      return;
    }
    tap(e.target as HTMLElement);
    // ⭐ Снимок контекста берём В МОМЕНТ ОТПРАВКИ: он должен описывать ту беду, о
    // которой пишет человек, а не состояние экрана настроек через минуту.
    void reportProblem(text, snapshot() as Record<string, string>).then((sent) => {
      reportSent = sent;
      reportFailed = !sent;
      if (sent) {
        reportOpen = false;
        forgetServerError();               // эта беда описана — следующая заявка про свою
      }
      render(screen);
    });
  });
}
